'use client';
import { useMotionValue, useMotionTemplate, motion, MotionValue } from 'motion/react';
import React from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';

export const EvervaultCard = ({ src, className }: { src?: string; className?: string }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  function onMouseMove({
    currentTarget,
    clientX,
    clientY,
  }: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const { left, top } = currentTarget.getBoundingClientRect();
    const x = clientX - left;
    const y = clientY - top;
    mouseX.set(x);
    mouseY.set(y);
  }

  return (
    <div
      className={cn(
        'p-0.5  bg-transparent aspect-square  flex items-center justify-center w-full h-full max-h-[200px] relative',
        className
      )}
    >
      <div
        onMouseMove={onMouseMove}
        className='group/card rounded-3xl w-full relative overflow-hidden bg-transparent flex items-center justify-center h-full'
      >
        <CardPattern mouseX={mouseX} mouseY={mouseY} />
        <div className='relative z-10 flex items-center justify-center'>
          <div className='relative h-44 w-44  rounded-full flex items-center justify-center text-white font-bold text-4xl'>
            <Image
              className='z-20'
              src={src || '/logo.svg'}
              alt='Uncommon12 logo'
              id='join-logo'
              width={176}
              height={176}
              priority={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export function CardPattern({ mouseX, mouseY }: { mouseX: MotionValue; mouseY: MotionValue }) {
  const maskImage = useMotionTemplate`radial-gradient(250px at ${mouseX}px ${mouseY}px, white, transparent)`;
  const style = { maskImage, WebkitMaskImage: maskImage };

  const LONG_STRING = React.useMemo(() => generateRepeatedString('Uncommon12', 3000), []);
  const SLICE_LEN = 1500;

  const [slice, setSlice] = React.useState(''); // same on server & client

  React.useEffect(() => {
    const max = Math.max(0, LONG_STRING.length - SLICE_LEN);
    const offset = Math.floor(Math.random() * (max || 1));
    setSlice(LONG_STRING.slice(offset, offset + SLICE_LEN));
  }, [LONG_STRING]);

  return (
    <div className="pointer-events-none">
      <div className="absolute inset-0 rounded-2xl bg-[#a0a0a0]/10 dark:bg-transparent dark:[mask-image:linear-gradient(white,transparent)] [mask-image:none] group-hover/card:opacity-50" />
      <motion.div
        className="absolute inset-0 rounded-2xl bg-[linear-gradient(90deg,#BBD8E6,#FCD2C3,#F2D5A3,#AED8C7)] opacity-0 group-hover/card:opacity-100 backdrop-blur-xl transition duration-500"
        style={style}
      />
      <motion.div className="absolute inset-0 rounded-2xl opacity-0 mix-blend-overlay group-hover/card:opacity-100" style={style}>
        <p className="absolute inset-x-0 text-xs h-full break-words whitespace-pre-wrap text-white font-mono font-bold transition duration-500">
          {slice}
        </p>
      </motion.div>
    </div>
  );
}

export const generateRepeatedString = (word: string, length: number) => {
  let repeated = '';
  while (repeated.length < length) {
    repeated += word;
  }
  return repeated.slice(0, length);
};


